import type { OnboardingState, SiteInfo, RecordedStep } from './types';
import { validateApiKey, finalizeRecording } from './api';
import { saveApiKey, getApiKey, clearApiKey } from './storage';
import { Recorder } from './recorder';

/**
 * Onboarding component - manages the 3-step onboarding flow
 */
export class Onboarding {
  private state: OnboardingState;
  private container: HTMLDivElement;
  private onStateChange?: (state: OnboardingState) => void;
  private recorder: Recorder | null = null;
  private phantomModeEnabled = false;

  constructor(onStateChange?: (state: OnboardingState) => void) {
    this.onStateChange = onStateChange;

    // Clean up old cached site info (backwards compatibility)
    try {
      sessionStorage.removeItem('warpway:siteInfo');
    } catch (error) {
      // Ignore errors
    }

    // Check if we have a saved API key
    const savedKey = getApiKey();

    if (savedKey) {
      // We have a key - fetch fresh site info from API
      this.state = {
        step: 'validate-key',
        apiKey: savedKey,
        isValidating: true,
      };
    } else {
      this.state = {
        step: 'get-key',
        isValidating: false,
      };
    }

    this.container = this.createContainer();

    // Now render initial content
    this.render();

    // If we have a saved key, validate it to get fresh site info
    if (savedKey) {
      this.refreshSiteInfo(savedKey);
    }
  }

  /**
   * Refresh site info from the API (called on load if API key exists)
   */
  private async refreshSiteInfo(apiKey: string): Promise<void> {
    try {
      const siteInfo = await validateApiKey(apiKey);

      // Update state with fresh site info
      this.setState({
        step: 'ready-to-record',
        apiKey,
        siteInfo,
        isValidating: false,
      });

      console.log('[Warpway SDK] Site info refreshed on load', siteInfo);
    } catch (error) {
      console.error('[Warpway SDK] Failed to refresh site info, clearing saved key', error);

      // Key is invalid - clear it and go back to get-key step
      clearApiKey();
      this.setState({
        step: 'get-key',
        apiKey: undefined,
        siteInfo: undefined,
        isValidating: false,
        error: 'Your session has expired. Please enter your API key again.',
      });
    }
  }

  /**
   * Create the onboarding container
   */
  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'warpway-onboarding';
    // Don't render yet - will be done after container is assigned
    return container;
  }

  /**
   * Get the DOM element
   */
  getElement(): HTMLDivElement {
    return this.container;
  }

  /**
   * Update state and re-render
   */
  private setState(updates: Partial<OnboardingState>): void {
    this.state = { ...this.state, ...updates };
    this.render();

    if (this.onStateChange) {
      this.onStateChange(this.state);
    }
  }

  /**
   * Render the current step
   */
  private render(): void {
    switch (this.state.step) {
      case 'get-key':
        this.container.innerHTML = this.renderGetKeyStep();
        break;
      case 'validate-key':
        this.container.innerHTML = this.renderValidateKeyStep();
        break;
      case 'ready-to-record':
        this.container.innerHTML = this.renderReadyStep();
        break;
      case 'recording':
        this.container.innerHTML = this.renderRecordingStep();
        break;
      case 'finalize-recording':
        this.container.innerHTML = this.renderFinalizeStep();
        break;
      case 'success':
        this.container.innerHTML = this.renderSuccessStep();
        break;
    }

    this.attachEventListeners();
  }

  /**
   * Render Step 1: Get API Key (Consolidated with paste step)
   */
  private renderGetKeyStep(): string {
    return `
      <div class="warpway-welcome">
        <p>👋 Welcome to Warpway!</p>
        <p class="warpway-subtitle">Get started with recording actions:</p>
      </div>
      <div class="warpway-step">
        <div class="warpway-step-number">1</div>
        <h4 class="warpway-step-title">Get your API key</h4>
        <p class="warpway-step-description">
          Get your API key from <a href="#" data-action="open-keys-page" class="warpway-inline-link">Settings → API Keys</a> and paste it below.
        </p>
        <input
          type="text"
          class="warpway-input"
          id="warpway-api-key-input"
          placeholder="Paste your API key here..."
        />
        <button class="warpway-btn warpway-btn-primary" data-action="validate-key">
          Continue →
        </button>
        <p class="warpway-step-hint">
          The link above will open your dashboard in a new tab
        </p>
      </div>
      <div class="warpway-step warpway-step-disabled">
        <div class="warpway-step-number">2</div>
        <h4 class="warpway-step-title">Record your first action</h4>
        <p class="warpway-step-description text-muted">Locked until key is validated</p>
      </div>
      <div class="warpway-footer">
        <a href="https://docs.warpway.ai" target="_blank" class="warpway-link">
          📚 View Documentation
        </a>
      </div>
    `;
  }

  /**
   * Render Step 2: Validate Key (shown while validating)
   */
  private renderValidateKeyStep(): string {
    const errorHtml = this.state.error
      ? `<div class="warpway-error">${this.state.error}</div>`
      : '';

    return `
      <div class="warpway-step warpway-step-active">
        <div class="warpway-step-number">1</div>
        <h4 class="warpway-step-title">Get your API key</h4>
        <p class="warpway-step-description">
          Get your API key from <a href="#" data-action="open-keys-page" class="warpway-inline-link">Settings → API Keys</a> and paste it below.
        </p>
        ${errorHtml}
        <input
          type="text"
          class="warpway-input"
          id="warpway-api-key-input"
          placeholder="Paste your API key here..."
          ${this.state.isValidating ? 'disabled' : ''}
        />
        <button
          class="warpway-btn warpway-btn-primary"
          data-action="validate-key"
          ${this.state.isValidating ? 'disabled' : ''}
        >
          ${this.state.isValidating ? 'Validating...' : 'Continue →'}
        </button>
        <p class="warpway-step-hint">
          The link above will open your dashboard in a new tab
        </p>
      </div>
      <div class="warpway-step warpway-step-disabled">
        <div class="warpway-step-number">2</div>
        <h4 class="warpway-step-title">Record your first action</h4>
        <p class="warpway-step-description text-muted">Locked until key is validated</p>
      </div>
    `;
  }

  /**
   * Render Step 2: Ready to Record
   */
  private renderReadyStep(): string {
    const siteInfo = this.state.siteInfo;

    return `
      <div class="warpway-step warpway-step-complete">
        <div class="warpway-step-number">✓</div>
        <h4 class="warpway-step-title">API key validated</h4>
        <p class="warpway-step-description text-muted">Complete</p>
      </div>
      <div class="warpway-site-info">
        <div class="warpway-site-info-row">
          <span class="warpway-label">Site:</span>
          <span class="warpway-value">${siteInfo?.name || 'Unknown'}</span>
        </div>
        <div class="warpway-site-info-row">
          <span class="warpway-label">Plan:</span>
          <span class="warpway-value">${siteInfo?.plan || 'free'}</span>
        </div>
      </div>
      <div class="warpway-step warpway-step-active">
        <div class="warpway-step-number">2</div>
        <h4 class="warpway-step-title">Record your first action</h4>
        <p class="warpway-step-description">
          Click the button below to start recording user interactions.
        </p>
        <button class="warpway-btn warpway-btn-primary" data-action="start-recording">
          🔴 Start Recording
        </button>
      </div>
      <div class="warpway-divider"></div>
      <div class="warpway-actions">
        <button class="warpway-btn warpway-btn-text" data-action="logout">
          Change API Key
        </button>
      </div>
    `;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Open keys page button
    const openKeysBtn = this.container.querySelector('[data-action="open-keys-page"]');
    if (openKeysBtn) {
      openKeysBtn.addEventListener('click', () => this.handleOpenKeysPage());
    }

    // Validate key button
    const validateBtn = this.container.querySelector('[data-action="validate-key"]');
    if (validateBtn) {
      validateBtn.addEventListener('click', () => this.handleValidateKey());
    }

    // Back button
    const backBtn = this.container.querySelector('[data-action="back-to-get-key"]');
    if (backBtn) {
      backBtn.addEventListener('click', () => this.handleBackToGetKey());
    }

    // Start recording button
    const recordBtn = this.container.querySelector('[data-action="start-recording"]');
    if (recordBtn) {
      recordBtn.addEventListener('click', () => this.handleStartRecording());
    }

    // Stop recording button
    const stopRecordBtn = this.container.querySelector('[data-action="stop-recording"]');
    if (stopRecordBtn) {
      stopRecordBtn.addEventListener('click', () => this.handleStopRecording());
    }

    // Cancel recording button
    const cancelRecordBtn = this.container.querySelector('[data-action="cancel-recording"]');
    if (cancelRecordBtn) {
      cancelRecordBtn.addEventListener('click', () => this.handleCancelRecording());
    }

    // Finalize recording button
    const finalizeBtn = this.container.querySelector('[data-action="finalize-recording"]');
    if (finalizeBtn) {
      finalizeBtn.addEventListener('click', () => this.handleFinalizeRecording());
    }

    // Logout button
    const logoutBtn = this.container.querySelector('[data-action="logout"]');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => this.handleLogout());
    }

    // Enter key on API key input
    const apiKeyInput = this.container.querySelector('#warpway-api-key-input') as HTMLInputElement;
    if (apiKeyInput) {
      apiKeyInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.handleValidateKey();
        }
      });
    }

    // Enter key on action name input
    const actionNameInput = this.container.querySelector('#warpway-action-name') as HTMLInputElement;
    if (actionNameInput) {
      actionNameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.handleFinalizeRecording();
        }
      });
    }

    // Open dashboard button (success screen)
    const openDashboardBtn = this.container.querySelector('[data-action="open-dashboard"]');
    if (openDashboardBtn) {
      openDashboardBtn.addEventListener('click', () => this.handleOpenDashboard());
    }

    // Dismiss success button
    const dismissSuccessBtn = this.container.querySelector('[data-action="dismiss-success"]');
    if (dismissSuccessBtn) {
      dismissSuccessBtn.addEventListener('click', () => this.handleDismissSuccess());
    }

    // Toggle phantom click button
    const phantomToggleBtn = this.container.querySelector('[data-action="toggle-phantom-click"]');
    if (phantomToggleBtn) {
      phantomToggleBtn.addEventListener('click', () => this.handleTogglePhantomClick());
    }

    // Refresh page button
    const refreshPageBtn = this.container.querySelector('[data-action="refresh-page"]');
    if (refreshPageBtn) {
      refreshPageBtn.addEventListener('click', () => this.handleRefreshPage());
    }
  }

  /**
   * Handle toggling phantom click mode
   */
  private handleTogglePhantomClick(): void {
    if (this.recorder) {
      this.recorder.togglePhantomClickMode();
    }
  }

  /**
   * Handle refresh page action
   */
  private handleRefreshPage(): void {
    if (this.recorder) {
      // Add refresh step to recording (without actually refreshing)
      this.recorder.addRefreshStep();

      // Show feedback toast
      this.showRefreshToast();
    }
  }

  /**
   * Show toast notification for refresh action
   */
  private showRefreshToast(): void {
    // Check if toast already exists
    const existingToast = document.getElementById('warpway-refresh-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.id = 'warpway-refresh-toast';
    toast.innerHTML = `
      <div style="
        position: fixed;
        bottom: 80px;
        right: 20px;
        background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4);
        z-index: 2147483646;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        font-size: 14px;
        font-weight: 600;
        animation: warpway-toast-slide-in 0.3s ease-out;
      ">
        🔄 Refresh action recorded
      </div>
    `;

    // Add animation
    const animStyle = document.createElement('style');
    animStyle.textContent = `
      @keyframes warpway-toast-slide-in {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(animStyle);

    document.body.appendChild(toast);

    // Remove after 2 seconds
    setTimeout(() => {
      toast.remove();
      animStyle.remove();
    }, 2000);
  }

  /**
   * Handle opening the keys page
   */
  private handleOpenKeysPage(): void {
    const hostname = window.location.hostname;
    const keysUrl = (hostname === 'localhost' || hostname === '127.0.0.1')
      ? 'http://localhost:3001/settings/keys'
      : 'https://app.warpway.ai/settings/keys';

    window.open(keysUrl, '_blank');
    // Don't auto-advance - stay on the same step so they can paste the key
  }

  /**
   * Handle going back to get key step
   */
  private handleBackToGetKey(): void {
    this.setState({ step: 'get-key', error: undefined });
  }

  /**
   * Handle validating the API key
   */
  private async handleValidateKey(): Promise<void> {
    const input = this.container.querySelector('#warpway-api-key-input') as HTMLInputElement;
    const apiKey = input?.value.trim();

    if (!apiKey) {
      this.setState({ error: 'Please enter an API key' });
      return;
    }

    this.setState({ isValidating: true, error: undefined });

    try {
      const siteInfo = await validateApiKey(apiKey);

      // Save API key to storage (site info is fetched fresh on each load)
      saveApiKey(apiKey);

      // Update state
      this.setState({
        step: 'ready-to-record',
        apiKey,
        siteInfo,
        isValidating: false,
      });

      console.log('[Warpway SDK] API key validated successfully', siteInfo);
    } catch (error) {
      this.setState({
        isValidating: false,
        error: error instanceof Error ? error.message : 'Failed to validate API key',
      });
    }
  }

  /**
   * Handle starting recording
   */
  private async handleStartRecording(): Promise<void> {
    console.log('[Warpway SDK] Start recording clicked');

    if (!this.state.apiKey) {
      this.setState({ error: 'API key not found' });
      return;
    }

    // Initialize recorder with callbacks
    this.recorder = new Recorder(
      (step) => this.onStepRecorded(step),
      (enabled) => this.onPhantomModeChanged(enabled)
    );

    try {
      // Start recording (now handles backend recording creation internally)
      const session = await this.recorder.startRecording();

      // Update state to recording mode
      this.setState({
        step: 'recording',
        recordingSession: session,
      });

      console.log('[Warpway SDK] Recording started', { recordingId: session.id });
    } catch (error) {
      console.error('[Warpway SDK] Failed to start recording', error);
      this.setState({
        error: error instanceof Error ? error.message : 'Failed to start recording',
      });
      this.recorder = null;
    }
  }

  /**
   * Handle phantom mode change
   */
  private onPhantomModeChanged(enabled: boolean): void {
    this.phantomModeEnabled = enabled;
    this.render(); // Re-render to update button state
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    clearApiKey();
    this.setState({
      step: 'get-key',
      apiKey: undefined,
      siteInfo: undefined,
      error: undefined,
    });
  }

  /**
   * Callback when a step is recorded
   */
  private onStepRecorded(step: RecordedStep): void {
    // Update session in state
    if (this.state.recordingSession) {
      this.state.recordingSession.steps.push(step);
      this.render(); // Re-render to show new step
    }
  }

  /**
   * Handle stopping recording
   */
  private async handleStopRecording(): Promise<void> {
    if (!this.recorder || !this.state.recordingSession) return;

    try {
      this.setState({ isSyncing: true });

      // Stop recorder (now handles syncing remaining steps internally)
      const session = await this.recorder.stopRecording();
      this.recorder = null;

      if (session && session.steps.length > 0) {
        console.log('[Warpway SDK] Recording stopped', {
          recordingId: session.id,
          totalSteps: session.steps.length
        });

        // Move to finalize step
        this.setState({
          step: 'finalize-recording',
          recordingSession: session,
          isSyncing: false,
        });
      } else {
        // No steps recorded
        this.setState({
          error: 'No steps were recorded. Please try again.',
          step: 'ready-to-record',
          recordingSession: undefined,
          isSyncing: false,
        });
      }
    } catch (error) {
      console.error('[Warpway SDK] Failed to stop recording', error);
      this.setState({
        error: error instanceof Error ? error.message : 'Failed to save recording',
        isSyncing: false,
      });
      this.recorder = null;
    }
  }

  /**
   * Handle finalizing recording
   */
  private async handleFinalizeRecording(): Promise<void> {
    const nameInput = this.container.querySelector('#warpway-action-name') as HTMLInputElement;
    const descInput = this.container.querySelector('#warpway-action-desc') as HTMLTextAreaElement;

    const actionName = nameInput?.value.trim();

    if (!actionName) {
      this.setState({ error: 'Action name is required' });
      return;
    }

    if (!this.state.apiKey || !this.state.recordingSession?.id) {
      this.setState({ error: 'Recording session not found' });
      return;
    }

    this.setState({ isSyncing: true, error: undefined });

    try {
      const result = await finalizeRecording(this.state.apiKey, this.state.recordingSession.id, {
        name: actionName,
        description: descInput?.value.trim() || '',
        intents: [actionName.toLowerCase()],
        inputs: [],
      });

      console.log('[Warpway SDK] Recording finalized', result);

      // Build dashboard URL for this draft
      const hostname = window.location.hostname;
      const dashboardUrl = (hostname === 'localhost' || hostname === '127.0.0.1')
        ? `http://localhost:3001/drafts/${result.draftId}` // Dashboard will run on port 3001 locally
        : `https://app.warpway.ai/drafts/${result.draftId}`;

      // Show success and return to ready state
      this.setState({
        step: 'ready-to-record',
        recordingSession: undefined,
        isSyncing: false,
        error: undefined,
      });

      // Show success notification in the SDK UI instead of browser alert
      this.showSuccessNotification(actionName, dashboardUrl);
    } catch (error) {
      console.error('[Warpway SDK] Failed to finalize recording', error);
      this.setState({
        isSyncing: false,
        error: error instanceof Error ? error.message : 'Failed to finalize recording',
      });
    }
  }

  /**
   * Handle canceling recording
   */
  private async handleCancelRecording(): Promise<void> {
    if (this.recorder) {
      try {
        await this.recorder.stopRecording();
      } catch (error) {
        console.error('[Warpway SDK] Error while canceling recording', error);
        // Ignore errors when canceling
      }
      this.recorder = null;
    }

    this.setState({
      step: 'ready-to-record',
      recordingSession: undefined,
      error: undefined,
    });
  }

  /**
   * Show success notification with link to dashboard
   */
  private showSuccessNotification(actionName: string, dashboardUrl: string): void {
    // Create a temporary success view
    this.setState({
      step: 'success',
      error: undefined,
    });

    // Store the URL for the button click handler
    (this.container as any)._dashboardUrl = dashboardUrl;

    // Render the success view
    this.render();

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      if (this.state.step === 'success') {
        this.setState({ step: 'ready-to-record' });
        this.render();
      }
    }, 10000);
  }

  /**
   * Handle opening the dashboard
   */
  private handleOpenDashboard(): void {
    const dashboardUrl = (this.container as any)._dashboardUrl || 'https://app.warpway.ai/drafts';
    window.open(dashboardUrl, '_blank');
    this.handleDismissSuccess();
  }

  /**
   * Handle dismissing success notification
   */
  private handleDismissSuccess(): void {
    this.setState({ step: 'ready-to-record' });
  }

  /**
   * Render Recording Step
   */
  private renderRecordingStep(): string {
    const session = this.state.recordingSession;
    const stepCount = session?.steps.length || 0;

    const stepsHtml = session?.steps
      .slice()
      .reverse()
      .slice(0, 10) // Show last 10 steps
      .map(
        (step) => `
        <div class="warpway-recorded-step">
          <span class="warpway-step-icon">${this.getStepIcon(step.type, step.metadata?.phantom)}</span>
          <div class="warpway-step-details">
            <div class="warpway-step-type">${step.type}${step.metadata?.phantom ? ' (phantom)' : ''}</div>
            ${step.metadata?.text ? `<div class="warpway-step-text">"${step.metadata.text.substring(0, 30)}"</div>` : ''}
            ${step.value ? `<div class="warpway-step-value">Value: ${step.value.substring(0, 20)}</div>` : ''}
          </div>
        </div>
      `
      )
      .join('') || '<p class="warpway-step-hint">No steps recorded yet. Start interacting with the page!</p>';

    const phantomBtnClass = this.phantomModeEnabled
      ? 'warpway-btn warpway-btn-phantom warpway-btn-phantom-active'
      : 'warpway-btn warpway-btn-phantom';

    const phantomBtnText = this.phantomModeEnabled
      ? '👻 Phantom Mode: ON (next click won\'t navigate)'
      : '👻 Enable Phantom Click';

    return `
      <div class="warpway-recording-header">
        <div class="warpway-recording-indicator">
          <span class="warpway-recording-dot"></span>
          <h3>Recording...</h3>
        </div>
        <div class="warpway-recording-info">
          <span>${stepCount} steps captured</span>
        </div>
      </div>
      <div class="warpway-phantom-controls">
        <button class="${phantomBtnClass}" data-action="toggle-phantom-click">
          ${phantomBtnText}
        </button>
        <button class="warpway-btn warpway-btn-phantom" data-action="refresh-page">
          🔄 Refresh Page
        </button>
        <p class="warpway-phantom-hint">
          💡 Use phantom click to record links without navigating, or refresh to reload the page
        </p>
      </div>
      <div class="warpway-recording-steps">
        ${stepsHtml}
      </div>
      <div class="warpway-recording-actions">
        <button class="warpway-btn warpway-btn-primary" data-action="stop-recording" ${this.state.isSyncing ? 'disabled' : ''}>
          ${this.state.isSyncing ? 'Saving...' : '⏹️ Stop & Save'}
        </button>
        <button class="warpway-btn warpway-btn-secondary" data-action="cancel-recording" ${this.state.isSyncing ? 'disabled' : ''}>
          Cancel
        </button>
      </div>
      <p class="warpway-step-hint">
        💡 Interact with the page to record actions
      </p>
    `;
  }

  /**
   * Render Finalize Recording Step
   */
  private renderFinalizeStep(): string {
    const session = this.state.recordingSession;
    const stepCount = session?.steps.length || 0;
    const errorHtml = this.state.error
      ? `<div class="warpway-error">${this.state.error}</div>`
      : '';

    return `
      <div class="warpway-finalize-header">
        <h3>✓ Recording Complete</h3>
        <p class="warpway-subtitle">Captured ${stepCount} steps</p>
      </div>
      ${errorHtml}
      <div class="warpway-finalize-form">
        <div class="warpway-form-group">
          <label for="warpway-action-name">Action Name *</label>
          <input
            type="text"
            id="warpway-action-name"
            class="warpway-input"
            placeholder="e.g., Update Email Address"
            ${this.state.isSyncing ? 'disabled' : ''}
          />
        </div>
        <div class="warpway-form-group">
          <label for="warpway-action-desc">Description (optional)</label>
          <textarea
            id="warpway-action-desc"
            class="warpway-input"
            rows="3"
            placeholder="What does this action do?"
            ${this.state.isSyncing ? 'disabled' : ''}
          ></textarea>
        </div>
      </div>
      <div class="warpway-finalize-actions">
        <button
          class="warpway-btn warpway-btn-primary"
          data-action="finalize-recording"
          ${this.state.isSyncing ? 'disabled' : ''}
        >
          ${this.state.isSyncing ? 'Saving...' : 'Save Action'}
        </button>
        <button
          class="warpway-btn warpway-btn-secondary"
          data-action="cancel-recording"
          ${this.state.isSyncing ? 'disabled' : ''}
        >
          Cancel
        </button>
      </div>
    `;
  }

  /**
   * Render Success Step
   */
  private renderSuccessStep(): string {
    const dashboardUrl = (this.container as any)._dashboardUrl || 'https://app.warpway.ai/drafts';

    return `
      <div class="warpway-success-container">
        <div class="warpway-success-icon">✓</div>
        <h3>Recording Saved Successfully!</h3>
        <p class="warpway-subtitle">Your action draft has been created.</p>

        <div class="warpway-success-info">
          <p><strong>⚠️ Important:</strong> This action is NOT published yet.</p>
          <p>Visit the dashboard to review, configure, and publish your action.</p>
        </div>

        <div class="warpway-success-actions">
          <button
            class="warpway-btn warpway-btn-primary"
            data-action="open-dashboard"
          >
            Open Dashboard →
          </button>
          <button
            class="warpway-btn warpway-btn-secondary"
            data-action="dismiss-success"
          >
            Dismiss
          </button>
        </div>

        <p class="warpway-step-hint">This message will auto-dismiss in 10 seconds</p>
      </div>
    `;
  }

  /**
   * Get icon for step type
   */
  private getStepIcon(type: string, isPhantom?: boolean): string {
    // Show ghost icon for phantom clicks
    if (type === 'click' && isPhantom) {
      return '👻';
    }

    switch (type) {
      case 'click':
        return '👆';
      case 'fill':
        return '✏️';
      case 'navigate':
        return '🔗';
      case 'waitFor':
        return '⏱️';
      case 'assert:text':
        return '✓';
      case 'refresh':
        return '🔄';
      default:
        return '•';
    }
  }

  /**
   * Get current state
   */
  getState(): OnboardingState {
    return { ...this.state };
  }
}
